import React, { useState } from 'react';
import {
X,
Home,
Edit,
Check,
XCircle,
Calendar,
User,
Phone,
Mail,
DollarSign,
FileText,
Image as ImageIcon,
History,
MapPin,
Maximize,
Eye,
Compass,
Car,
Loader
} from 'lucide-react';

interface UnitDetailModalProps {
unit: any;
onClose: () => void;
onUpdate: () => void;
}

const UnitDetailModal: React.FC<UnitDetailModalProps> = ({ unit, onClose, onUpdate }) => {
const [activeTab, setActiveTab] = useState('info');
const [showReserveForm, setShowReserveForm] = useState(false);
const [showSellForm, setShowSellForm] = useState(false);
const [showEditForm, setShowEditForm] = useState(false);
const [loading, setLoading] = useState(false);
const [error, setError] = useState<string | null>(null);

// Formulaire réservation
const [reserveData, setReserveData] = useState({
client_name: '',
client_phone: '',
client_email: '',
reservation_date: new Date().toISOString().split('T')[0],
deposit_amount: '',
notes: '',
});

// Formulaire vente
const [sellData, setSellData] = useState({
client_name: unit.client_name || '',
final_price: unit.final_price,
sale_date: new Date().toISOString().split('T')[0],
payment_method: 'cash',
commission_amount: '',
notes: '',
});

// Formulaire édition
const [editData, setEditData] = useState({
area: unit.area,
final_price: unit.final_price,
orientation: unit.orientation,
view: unit.view,
has_balcony: unit.has_balcony,
balcony_area: unit.balcony_area || 0,
has_parking: unit.has_parking,
parking_number: unit.parking_number || '',
});

// Actions status
const handleReserve = async () => {
if (!reserveData.client_name || !reserveData.client_phone) {
setError('Nom et téléphone du client sont obligatoires');
return;
}

const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
setLoading(true);
setError(null);

try {
  const response = await fetch(`${apiUrl}/promotions/units/${unit.id}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      status: 'reserved',
      ...reserveData,
    }),
  });

  if (!response.ok) throw new Error('Erreur lors de la réservation');

  setShowReserveForm(false);
  onUpdate();
  onClose();
} catch (err: any) {
  setError(err.message);
} finally {
  setLoading(false);
}
};

const handleSell = async () => {
if (!sellData.client_name) {
setError('Nom du client est obligatoire');
return;
}

const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
setLoading(true);
setError(null);

try {
  const response = await fetch(`${apiUrl}/promotions/units/${unit.id}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      status: 'sold',
      ...sellData,
    }),
  });

  if (!response.ok) throw new Error('Erreur lors de la vente');

  setShowSellForm(false);
  onUpdate();
  onClose();
} catch (err: any) {
  setError(err.message);
} finally {
  setLoading(false);
}
};

const handleCancelReservation = async () => {
if (!confirm('Êtes-vous sûr de vouloir annuler cette réservation ?')) return;

const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
setLoading(true);
setError(null);

try {
  const response = await fetch(`${apiUrl}/promotions/units/${unit.id}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      status: 'available',
      client_name: null,
      client_phone: null,
      client_email: null,
    }),
  });

  if (!response.ok) throw new Error('Erreur lors de l\'annulation');

  onUpdate();
  onClose();
} catch (err: any) {
  setError(err.message);
} finally {
  setLoading(false);
}
};

const handleEdit = async () => {
const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
setLoading(true);
setError(null);

try {
  const response = await fetch(`${apiUrl}/promotions/units/${unit.id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(editData),
  });

  if (!response.ok) throw new Error('Erreur lors de la modification');

  setShowEditForm(false);
  onUpdate();
  onClose();
} catch (err: any) {
  setError(err.message);
} finally {
  setLoading(false);
}
};

const statusConfig = {
available: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', label: 'Disponible' },
reserved: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800 dark:text-orange-300', label: 'Réservé' },
sold: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300', label: 'Vendu' },
unavailable: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-300', label: 'Indisponible' },
};

const config = statusConfig[unit.status as keyof typeof statusConfig];

return (
<div className="fixed inset-0 z-50 overflow-y-auto">
{/* Overlay */}
<div className="fixed inset-0 bg-black/50" onClick={onClose} />


  {/* Modal */}
  <div className="relative min-h-screen flex items-center justify-center p-0 sm:p-4">
    <div className="relative bg-white dark:bg-gray-800 rounded-none sm:rounded-lg shadow-xl max-w-4xl w-full h-screen sm:h-auto sm:max-h-[90vh] overflow-hidden">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 sm:px-6 py-4 flex items-center justify-between z-10">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Lot {unit.unit_number}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Bloc {unit.block_name} • {unit.floor === 0 ? 'RDC' : `Étage ${unit.floor}`}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <X className="w-7 h-7 sm:w-6 sm:h-6 text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b dark:border-gray-700 px-2 sm:px-6 overflow-x-auto">
        <div className="flex space-x-2 sm:space-x-4">
          {[
            { id: 'info', label: 'Informations', icon: Home },
            { id: 'details', label: 'Caractéristiques', icon: FileText },
            { id: 'media', label: 'Photos', icon: ImageIcon },
            { id: 'history', label: 'Historique', icon: History },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-3 sm:px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="text-sm font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/30 border-2 border-red-500 rounded-lg p-4 flex items-start space-x-3">
            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-red-900 dark:text-red-100">Erreur</h4>
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        )}

        {/* Tab: Informations */}
        {activeTab === 'info' && (
          <div className="space-y-6">
            {/* Statut et Prix */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Statut
                </label>
                <span className={`inline-flex px-4 py-2 rounded-lg text-sm font-medium ${config.bg} ${config.text}`}>
                  {config.label}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Prix
                </label>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {new Intl.NumberFormat('fr-DZ').format(unit.final_price)} DA
                </p>
              </div>
            </div>

            {/* Infos client si réservé/vendu */}
            {(unit.status === 'reserved' || unit.status === 'sold') && unit.client_name && (
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-3">
                  Informations client
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-sm">
                    <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-blue-900 dark:text-blue-100">{unit.client_name}</span>
                  </div>
                  {unit.client_phone && (
                    <div className="flex items-center space-x-2 text-sm">
                      <Phone className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-blue-900 dark:text-blue-100">{unit.client_phone}</span>
                    </div>
                  )}
                  {unit.client_email && (
                    <div className="flex items-center space-x-2 text-sm">
                      <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-blue-900 dark:text-blue-100">{unit.client_email}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Grille infos principales */}
            <div className="grid grid-cols-2 gap-4">
              <InfoItem icon={Home} label="Type" value={`${unit.unit_type.toUpperCase()}${unit.is_duplex ? ' Duplex' : ''}${unit.is_triplex ? ' Triplex' : ''}`} />
              <InfoItem icon={Maximize} label="Surface" value={`${unit.area} m²`} />
              <InfoItem icon={Compass} label="Orientation" value={unit.orientation || 'Non renseignée'} />
              <InfoItem icon={Eye} label="Vue" value={unit.view || 'Non renseignée'} />
              {unit.has_balcony && (
                <InfoItem icon={Home} label="Balcon" value={`${unit.balcony_area || 0} m²`} />
              )}
              {unit.has_parking && (
                <InfoItem icon={Car} label="Parking" value={unit.parking_number || 'Oui'} />
              )}
            </div>
          </div>
        )}

        {/* Tab: Caractéristiques */}
        {activeTab === 'details' && (
          <div className="space-y-4">
            <DetailRow label="Nombre de pièces" value={unit.rooms || 'Non renseigné'} />
            <DetailRow label="Chambres" value={unit.bedrooms || 'Non renseigné'} />
            <DetailRow label="Salles de bain" value={unit.bathrooms || 'Non renseigné'} />
            <DetailRow label="Surface totale" value={`${unit.area} m²`} />
            {unit.has_balcony && <DetailRow label="Surface balcon" value={`${unit.balcony_area} m²`} />}
            {unit.has_terrace && <DetailRow label="Surface terrasse" value={`${unit.terrace_area} m²`} />}
            <DetailRow label="Étages occupés" value={unit.floor_span || 1} />
            <DetailRow label="Prix au m²" value={`${new Intl.NumberFormat('fr-DZ').format(Math.round(unit.final_price / unit.area))} DA/m²`} />
          </div>
        )}

        {/* Tab: Photos */}
        {activeTab === 'media' && (
          <div>
            {unit.photos && unit.photos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {unit.photos.map((photo: string, index: number) => (
                  <div key={index} className="relative aspect-square group">
                    <img
                      src={photo}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <a
                      href={photo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg"
                    >
                      <Eye className="w-8 h-8 text-white" />
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 dark:text-gray-400 text-center py-8">
                Aucune photo disponible pour ce lot
              </p>
            )}
          </div>
        )}

        {/* Tab: Historique */}
        {activeTab === 'history' && (
          <div>
            <p className="text-gray-600 dark:text-gray-400 text-center py-8">
              Historique des actions à venir...
            </p>
          </div>
        )}
      </div>

      {/* Actions Footer */}
      <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t dark:border-gray-700 px-6 py-4">
        {!showReserveForm && !showSellForm && !showEditForm && (
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              Fermer
            </button>

            <div className="flex items-center space-x-3">
              {unit.status === 'available' && (
                <button
                  onClick={() => setShowReserveForm(true)}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center space-x-2"
                >
                  <Calendar className="w-4 h-4" />
                  <span>Réserver</span>
                </button>
              )}

              {(unit.status === 'available' || unit.status === 'reserved') && (
                <button
                  onClick={() => setShowSellForm(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                >
                  <Check className="w-4 h-4" />
                  <span>Vendre</span>
                </button>
              )}

              {unit.status === 'reserved' && (
                <button
                  onClick={handleCancelReservation}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2 disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" />
                  <span>Annuler réservation</span>
                </button>
              )}

              <button
                onClick={() => setShowEditForm(true)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center space-x-2"
              >
                <Edit className="w-4 h-4" />
                <span>Modifier</span>
              </button>
            </div>
          </div>
        )}

        {/* Formulaire Réservation */}
        {showReserveForm && (
          <ReserveForm
            data={reserveData}
            onChange={setReserveData}
            onSubmit={handleReserve}
            onCancel={() => setShowReserveForm(false)}
            loading={loading}
          />
        )}

        {/* Formulaire Vente */}
        {showSellForm && (
          <SellForm
            data={sellData}
            onChange={setSellData}
            onSubmit={handleSell}
            onCancel={() => setShowSellForm(false)}
            loading={loading}
          />
        )}

        {/* Formulaire Édition */}
        {showEditForm && (
          <EditForm
            data={editData}
            onChange={setEditData}
            onSubmit={handleEdit}
            onCancel={() => setShowEditForm(false)}
            loading={loading}
          />
        )}
      </div>
    </div>
  </div>
</div>
);
};

// Composants helpers
const InfoItem: React.FC<{ icon: any; label: string; value: string }> = ({ icon: Icon, label, value }) => (

<div className="flex items-start space-x-3"> <Icon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" /> <div> <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p> <p className="text-base font-medium text-gray-900 dark:text-white capitalize">{value}</p> </div> </div> );
const DetailRow: React.FC<{ label: string; value: any }> = ({ label, value }) => (

<div className="flex items-center justify-between py-3 border-b dark:border-gray-700 last:border-0"> <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span> <span className="text-sm font-medium text-gray-900 dark:text-white">{value}</span> </div> );
// Formulaire Réservation
const ReserveForm: React.FC<any> = ({ data, onChange, onSubmit, onCancel, loading }) => (

<div className="space-y-4"> <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Réserver ce lot</h3>

<div className="grid grid-cols-2 gap-4">
  <input
    type="text"
    placeholder="Nom du client *"
    value={data.client_name}
    onChange={(e) => onChange({ ...data, client_name: e.target.value })}
    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
  />
  <input
    type="tel"
    placeholder="Téléphone *"
    value={data.client_phone}
    onChange={(e) => onChange({ ...data, client_phone: e.target.value })}
    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
  />
  <input
    type="email"
    placeholder="Email"
    value={data.client_email}
    onChange={(e) => onChange({ ...data, client_email: e.target.value })}
    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
  />
  <input
    type="number"
    placeholder="Acompte (DA)"
    value={data.deposit_amount}
    onChange={(e) => onChange({ ...data, deposit_amount: e.target.value })}
    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
  />
</div>

<textarea
  placeholder="Notes..."
  value={data.notes}
  onChange={(e) => onChange({ ...data, notes: e.target.value })}
  rows={3}
  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
/>

<div className="flex justify-end space-x-3">
  <button
    onClick={onCancel}
    disabled={loading}
    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
  >
    Annuler
  </button>
  <button
    onClick={onSubmit}
    disabled={loading}
    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center space-x-2"
  >
    {loading && <Loader className="w-4 h-4 animate-spin" />}
    <span>Confirmer la réservation</span>
  </button>
</div>
</div> );
// Formulaire Vente
const SellForm: React.FC<any> = ({ data, onChange, onSubmit, onCancel, loading }) => (

<div className="space-y-4"> <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Vendre ce lot</h3>

<div className="grid grid-cols-2 gap-4">
  <input
    type="text"
    placeholder="Nom du client *"
    value={data.client_name}
    onChange={(e) => onChange({ ...data, client_name: e.target.value })}
    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
  />
  <input
    type="number"
    placeholder="Prix final (DA)"
    value={data.final_price}
    onChange={(e) => onChange({ ...data, final_price: parseInt(e.target.value) })}
    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
  />
  <select
    value={data.payment_method}
    onChange={(e) => onChange({ ...data, payment_method: e.target.value })}
    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
  >
    <option value="cash">Comptant</option>
    <option value="credit">Crédit</option>
    <option value="installment">Échelonné</option>
  </select>
  <input
    type="number"
    placeholder="Commission (DA)"
    value={data.commission_amount}
    onChange={(e) => onChange({ ...data, commission_amount: e.target.value })}
    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
  />
</div>

<textarea
  placeholder="Notes..."
  value={data.notes}
  onChange={(e) => onChange({ ...data, notes: e.target.value })}
  rows={3}
  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
/>

<div className="flex justify-end space-x-3">
  <button
    onClick={onCancel}
    disabled={loading}
    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
  >
    Annuler
  </button>
  <button
    onClick={onSubmit}
    disabled={loading}
    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
  >
    {loading && <Loader className="w-4 h-4 animate-spin" />}
    <span>Confirmer la vente</span>
  </button>
</div>
</div> );
// Formulaire Édition
const EditForm: React.FC<any> = ({ data, onChange, onSubmit, onCancel, loading }) => (

<div className="space-y-4"> <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Modifier le lot</h3>

<div className="grid grid-cols-2 gap-4">
  <input
    type="number"
    placeholder="Surface (m²)"
    value={data.area}
    onChange={(e) => onChange({ ...data, area: parseInt(e.target.value) })}
    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
  />
  <input
    type="number"
    placeholder="Prix (DA)"
    value={data.final_price}
    onChange={(e) => onChange({ ...data, final_price: parseInt(e.target.value) })}
    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
  />
  <input
    type="text"
    placeholder="Orientation"
    value={data.orientation}
    onChange={(e) => onChange({ ...data, orientation: e.target.value })}
    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
  />
  <input
    type="text"
    placeholder="Vue"
    value={data.view}
    onChange={(e) => onChange({ ...data, view: e.target.value })}
    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
  />
</div>

<div className="flex justify-end space-x-3">
  <button
    onClick={onCancel}
    disabled={loading}
    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
  >
    Annuler
  </button>
  <button
    onClick={onSubmit}
    disabled={loading}
    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center space-x-2"
  >
    {loading && <Loader className="w-4 h-4 animate-spin" />}
    <span>Enregistrer les modifications</span>
  </button>
</div>
</div> );
export default UnitDetailModal;
